import { Check, Globe } from "lucide-react";
import { useLocales, useLocaleState, useTranslate } from "ra-core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type LanguageSelectorProps = {
  className?: string;
};

/**
 * Glass language switcher for the auth screens.
 *
 * Lists whatever locales the i18nProvider advertises and renders nothing when
 * there is only one. Styled for the dark auth backdrop, unlike the in-app
 * <LocalesMenuButton />.
 */
export const LanguageSelector = ({ className }: LanguageSelectorProps) => {
  const locales = useLocales();
  const [locale, setLocale] = useLocaleState();
  const translate = useTranslate();

  if (locales.length <= 1) {
    return null;
  }

  const currentName = locales.find((l) => l.locale === locale)?.name;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={translate("crm.language", { _: "Language" })}
        className={cn(
          "flex items-center gap-1 px-2 py-1 rounded-md border border-white/20 bg-white/10 text-white text-sm transition-colors hover:bg-white/20",
          className,
        )}
      >
        <Globe className="w-4 h-4" />
        <span>{currentName ?? locale.toUpperCase()}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-32 border-white/20 bg-gray-800/95 text-gray-200 backdrop-blur-sm"
      >
        {locales.map((language) => (
          <DropdownMenuItem
            key={language.locale}
            onClick={() => setLocale(language.locale)}
            className={cn(
              "cursor-pointer text-sm focus:bg-white/10 focus:text-white",
              language.locale === locale && "bg-indigo-600/50 text-white",
            )}
          >
            {language.name}
            <Check
              className={cn(
                "ml-auto text-white",
                language.locale !== locale && "hidden",
              )}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
